/**
 * Jertscript2 Parser
 * A recursive descent parser for the French satirical programming language
 * that compiles to TypeScript.
 */

import { Token, TokenType } from '../lexer/tokens.js';
import {
  Program,
  Statement,
  Expression,
  TypeNode,
  TypeAnnotation,
  SimpleType,
  ArrayType,
  GenericType,
  UnionType,
  FunctionType,
  FunctionTypeParameter,
  Literal,
  Identifier,
  BinaryExpression,
  UnaryExpression,
  CallExpression,
  MemberExpression,
  AssignmentExpression,
  ArrayExpression,
  ObjectExpression,
  ObjectProperty,
  NewExpression,
  AwaitExpression,
  ThisExpression,
  GroupingExpression,
  TypeAssertion,
  ArrowFunction,
  ArrowFunctionParameter,
  VariableDeclaration,
  FunctionDeclaration,
  FunctionParameter,
  IfStatement,
  WhileStatement,
  ForStatement,
  ReturnStatement,
  BlockStatement,
  TryStatement,
  ClassDeclaration,
  ClassMember,
  MethodDefinition,
  PropertyDefinition,
  ConstructorDefinition,
  ImportStatement,
  ImportSpecifier,
  ExportStatement,
  ExportSpecifier,
  BreakStatement,
  ContinueStatement,
  ThrowStatement,
  ExpressionStatement,
  InterfaceDeclaration,
  InterfaceMember,
  PropertySignature,
  MethodSignature,
  TypeAliasDeclaration,
  EnumDeclaration,
  EnumMember,
  Visibility,
} from './ast.js';

/**
 * Parser error with location information
 */
export class ParseError extends Error {
  constructor(
    message: string,
    public line: number,
    public column: number
  ) {
    super(`[ligne ${line}, colonne ${column}] ${message}`);
    this.name = 'ParseError';
  }
}

/**
 * Parser class for Jertscript2
 * Implements recursive descent parsing with proper operator precedence
 */
export class Parser {
  private tokens: Token[];
  private current: number = 0;
  private errors: ParseError[] = [];

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  /**
   * Main entry point for parsing
   */
  parse(): Program {
    const statements: Statement[] = [];

    while (!this.isAtEnd()) {
      try {
        const stmt = this.declaration();
        if (stmt) {
          statements.push(stmt);
        }
      } catch (error) {
        if (error instanceof ParseError) {
          this.errors.push(error);
          this.synchronize();
        } else {
          throw error;
        }
      }
    }

    return {
      type: 'Program',
      body: statements,
      line: 1,
      column: 1,
    };
  }

  /**
   * Returns any parsing errors encountered
   */
  getErrors(): ParseError[] {
    return this.errors;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF;
  }

  private peek(): Token {
    return this.tokens[this.current];
  }

  private previous(): Token {
    return this.tokens[this.current - 1];
  }

  private advance(): Token {
    if (!this.isAtEnd()) {
      this.current++;
    }
    return this.previous();
  }

  private check(type: TokenType): boolean {
    if (this.isAtEnd()) return false;
    return this.peek().type === type;
  }

  private match(...types: TokenType[]): boolean {
    for (const type of types) {
      if (this.check(type)) {
        this.advance();
        return true;
      }
    }
    return false;
  }

  private consume(type: TokenType, message: string): Token {
    if (this.check(type)) {
      return this.advance();
    }
    throw this.error(this.peek(), message);
  }

  private error(token: Token, message: string): ParseError {
    return new ParseError(message, token.line, token.column);
  }

  /**
   * Error recovery - synchronize to next statement boundary
   */
  private synchronize(): void {
    this.advance();

    while (!this.isAtEnd()) {
      if (this.previous().type === TokenType.SEMICOLON) return;
      if (this.previous().type === TokenType.NEWLINE) return;

      switch (this.peek().type) {
        case TokenType.CLASSE:
        case TokenType.FONCTION:
        case TokenType.CROISSANT:
        case TokenType.BAGUETTE:
        case TokenType.POUR:
        case TokenType.FRANCHEMENT:
        case TokenType.PENDANT:
        case TokenType.RETOURNER:
        case TokenType.IMPORTER:
        case TokenType.EXPORTER:
        case TokenType.INTERFACE:
        case TokenType.TYPE:
        case TokenType.ENUMERATION:
          return;
      }

      this.advance();
    }
  }

  // ============================================================================
  // DECLARATION PARSING
  // ============================================================================

  private declaration(): Statement | null {
    // Skip newlines between statements
    while (this.match(TokenType.NEWLINE)) {
      // Skip
    }

    if (this.isAtEnd()) return null;

    if (this.match(TokenType.IMPORTER)) {
      return this.importStatement();
    }

    if (this.match(TokenType.EXPORTER)) {
      return this.exportStatement();
    }

    if (this.match(TokenType.INTERFACE)) {
      return this.interfaceDeclaration();
    }

    if (this.match(TokenType.TYPE)) {
      return this.typeAliasDeclaration();
    }

    if (this.match(TokenType.ENUMERATION)) {
      return this.enumDeclaration();
    }

    if (this.match(TokenType.CLASSE)) {
      return this.classDeclaration();
    }

    if (this.check(TokenType.ASYNCHRONE) && this.checkNext(TokenType.FONCTION)) {
      this.advance(); // consume 'asynchrone'
      this.advance(); // consume 'fonction'
      return this.functionDeclaration(true);
    }

    if (this.match(TokenType.FONCTION)) {
      return this.functionDeclaration(false);
    }

    if (this.match(TokenType.CROISSANT, TokenType.BAGUETTE)) {
      return this.variableDeclaration();
    }

    return this.statement();
  }

  private checkNext(type: TokenType): boolean {
    if (this.current + 1 >= this.tokens.length) return false;
    return this.tokens[this.current + 1].type === type;
  }

  // ============================================================================
  // IMPORT/EXPORT STATEMENTS
  // ============================================================================

  private importStatement(): ImportStatement {
    const line = this.previous().line;
    const column = this.previous().column;
    const specifiers: ImportSpecifier[] = [];
    let defaultImport: string | undefined;
    let namespaceImport: string | undefined;

    // import * comme nom de 'module'
    if (this.match(TokenType.STAR)) {
      this.consume(TokenType.COMME, "Attendu 'comme' apres '*'");
      const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom pour le namespace import');
      namespaceImport = nameToken.lexeme;
    }
    // import defaultName de 'module' OR import { a, b } de 'module'
    else if (this.check(TokenType.IDENTIFIER)) {
      const nameToken = this.advance();
      defaultImport = nameToken.lexeme;

      // Check if there are also named imports
      if (this.match(TokenType.COMMA)) {
        if (this.match(TokenType.LBRACE)) {
          this.parseImportSpecifiers(specifiers);
          this.consume(TokenType.RBRACE, "Attendu '}' apres les specifiers d'import");
        }
      }
    }
    // import { a, b } de 'module'
    else if (this.match(TokenType.LBRACE)) {
      this.parseImportSpecifiers(specifiers);
      this.consume(TokenType.RBRACE, "Attendu '}' apres les specifiers d'import");
    }

    this.consume(TokenType.DE, "Attendu 'de' apres les specifiers d'import");
    const sourceToken = this.consume(TokenType.STRING, "Attendu une chaine pour le chemin du module");
    const source = String(sourceToken.literal ?? sourceToken.lexeme.slice(1, -1));

    this.consumeStatementEnd();

    return {
      type: 'ImportStatement',
      specifiers,
      defaultImport,
      namespaceImport,
      source,
      line,
      column,
    };
  }

  private parseImportSpecifiers(specifiers: ImportSpecifier[]): void {
    do {
      if (this.check(TokenType.RBRACE)) break;

      const importedToken = this.consume(TokenType.IDENTIFIER, "Attendu un identifiant d'import");
      let imported = importedToken.lexeme;
      let local = imported;

      if (this.match(TokenType.COMME)) {
        const localToken = this.consume(TokenType.IDENTIFIER, "Attendu un alias apres 'comme'");
        local = localToken.lexeme;
      }

      specifiers.push({ imported, local });
    } while (this.match(TokenType.COMMA));
  }

  private exportStatement(): ExportStatement {
    const line = this.previous().line;
    const column = this.previous().column;
    let isDefault = false;

    // export defaut expression
    if (this.check(TokenType.IDENTIFIER) && this.peek().lexeme === 'defaut') {
      this.advance();
      isDefault = true;

      // export defaut classe/fonction/expression
      if (this.match(TokenType.CLASSE)) {
        return {
          type: 'ExportStatement',
          declaration: this.classDeclaration(),
          isDefault: true,
          line,
          column,
        };
      }

      if (this.check(TokenType.ASYNCHRONE) && this.checkNext(TokenType.FONCTION)) {
        this.advance(); // consume 'asynchrone'
        this.advance(); // consume 'fonction'
        return {
          type: 'ExportStatement',
          declaration: this.functionDeclaration(true),
          isDefault: true,
          line,
          column,
        };
      }

      if (this.match(TokenType.FONCTION)) {
        return {
          type: 'ExportStatement',
          declaration: this.functionDeclaration(false),
          isDefault: true,
          line,
          column,
        };
      }

      // Default export of expression
      const expr = this.expression();
      this.consumeStatementEnd();

      return {
        type: 'ExportStatement',
        declaration: {
          type: 'ExpressionStatement',
          expression: expr,
          line: expr.line,
          column: expr.column,
        },
        isDefault: true,
        line,
        column,
      };
    }

    // export { a, b }
    if (this.match(TokenType.LBRACE)) {
      const specifiers: ExportSpecifier[] = [];

      do {
        if (this.check(TokenType.RBRACE)) break;

        const localToken = this.consume(TokenType.IDENTIFIER, "Attendu un identifiant d'export");
        let local = localToken.lexeme;
        let exported = local;

        if (this.match(TokenType.COMME)) {
          const exportedToken = this.consume(TokenType.IDENTIFIER, "Attendu un alias apres 'comme'");
          exported = exportedToken.lexeme;
        }

        specifiers.push({ local, exported });
      } while (this.match(TokenType.COMMA));

      this.consume(TokenType.RBRACE, "Attendu '}' apres les specifiers d'export");
      this.consumeStatementEnd();

      return {
        type: 'ExportStatement',
        specifiers,
        isDefault: false,
        line,
        column,
      };
    }

    // export declaration
    let declaration: Statement | undefined;

    if (this.match(TokenType.CLASSE)) {
      declaration = this.classDeclaration();
    } else if (this.check(TokenType.ASYNCHRONE) && this.checkNext(TokenType.FONCTION)) {
      this.advance(); // consume 'asynchrone'
      this.advance(); // consume 'fonction'
      declaration = this.functionDeclaration(true);
    } else if (this.match(TokenType.FONCTION)) {
      declaration = this.functionDeclaration(false);
    } else if (this.match(TokenType.CROISSANT, TokenType.BAGUETTE)) {
      declaration = this.variableDeclaration();
    } else if (this.match(TokenType.INTERFACE)) {
      declaration = this.interfaceDeclaration();
    } else if (this.match(TokenType.TYPE)) {
      declaration = this.typeAliasDeclaration();
    } else if (this.match(TokenType.ENUMERATION)) {
      declaration = this.enumDeclaration();
    } else {
      throw this.error(this.peek(), 'Attendu une declaration apres export');
    }

    return {
      type: 'ExportStatement',
      declaration,
      isDefault: false,
      line,
      column,
    };
  }

  // ============================================================================
  // TYPE DECLARATIONS
  // ============================================================================

  private interfaceDeclaration(): InterfaceDeclaration {
    const line = this.previous().line;
    const column = this.previous().column;

    const nameToken = this.consume(TokenType.IDENTIFIER, "Attendu un nom pour l'interface");
    const name = nameToken.lexeme;

    let extendsClause: string[] | undefined;
    if (this.match(TokenType.ETEND)) {
      extendsClause = [];
      do {
        const extendToken = this.consume(TokenType.IDENTIFIER, "Attendu un nom d'interface apres 'etend'");
        extendsClause.push(extendToken.lexeme);
      } while (this.match(TokenType.COMMA));
    }

    this.consume(TokenType.LBRACE, "Attendu '{' pour le corps de l'interface");
    const members = this.parseInterfaceMembers();
    this.consume(TokenType.RBRACE, "Attendu '}' apres le corps de l'interface");

    return {
      type: 'InterfaceDeclaration',
      name,
      extends: extendsClause,
      members,
      line,
      column,
    };
  }

  private parseInterfaceMembers(): InterfaceMember[] {
    const members: InterfaceMember[] = [];

    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      // Skip newlines
      while (this.match(TokenType.NEWLINE)) {}
      if (this.check(TokenType.RBRACE)) break;

      let readonly = false;
      if (this.match(TokenType.LECTURE_SEULE)) {
        readonly = true;
      }

      const nameToken = this.consume(TokenType.IDENTIFIER, "Attendu un nom de membre d'interface");
      const name = nameToken.lexeme;

      let optional = false;
      if (this.match(TokenType.QUESTION)) {
        optional = true;
      }

      // Method signature: name(params): returnType
      if (this.check(TokenType.LPAREN)) {
        this.consume(TokenType.LPAREN, "Attendu '(' pour la signature de methode");
        const params = this.parseFunctionParams();
        this.consume(TokenType.RPAREN, "Attendu ')' apres les parametres");

        let returnType: TypeNode | undefined;
        if (this.match(TokenType.COLON)) {
          returnType = this.parseType();
        }

        members.push({
          name,
          params,
          returnType,
          optional,
        } as MethodSignature);
      }
      // Property signature: name: type
      else {
        this.consume(TokenType.COLON, "Attendu ':' apres le nom de propriete");
        const typeAnnotation = this.parseType();

        members.push({
          name,
          typeAnnotation,
          optional,
          readonly,
        } as PropertySignature);
      }

      // Optional semicolon or newline
      this.match(TokenType.SEMICOLON, TokenType.COMMA, TokenType.NEWLINE);
    }

    return members;
  }

  private typeAliasDeclaration(): TypeAliasDeclaration {
    const line = this.previous().line;
    const column = this.previous().column;

    const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom pour le type alias');
    const name = nameToken.lexeme;

    this.consume(TokenType.EQUALS, "Attendu '=' apres le nom du type");
    const typeValue = this.parseType();
    this.consumeStatementEnd();

    return {
      type: 'TypeAliasDeclaration',
      name,
      typeValue,
      line,
      column,
    };
  }

  private enumDeclaration(): EnumDeclaration {
    const line = this.previous().line;
    const column = this.previous().column;

    const nameToken = this.consume(TokenType.IDENTIFIER, "Attendu un nom pour l'enum");
    const name = nameToken.lexeme;

    this.consume(TokenType.LBRACE, "Attendu '{' pour le corps de l'enum");
    const members: EnumMember[] = [];

    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      // Skip newlines
      while (this.match(TokenType.NEWLINE)) {}
      if (this.check(TokenType.RBRACE)) break;

      const memberToken = this.consume(TokenType.IDENTIFIER, "Attendu un nom de membre d'enum");
      const memberName = memberToken.lexeme;
      let value: Expression | undefined;

      if (this.match(TokenType.EQUALS)) {
        value = this.expression();
      }

      members.push({ name: memberName, value });
      this.match(TokenType.COMMA, TokenType.NEWLINE);
    }

    this.consume(TokenType.RBRACE, "Attendu '}' apres le corps de l'enum");

    return {
      type: 'EnumDeclaration',
      name,
      members,
      line,
      column,
    };
  }

  // ============================================================================
  // TYPE PARSING
  // ============================================================================

  private parseType(): TypeNode {
    return this.parseUnionType();
  }

  private parseUnionType(): TypeNode {
    let left = this.parsePrimaryType();

    while (this.match(TokenType.PIPE)) {
      const right = this.parsePrimaryType();
      const types = left.type === 'UnionType' ? [...(left as UnionType).types, right] : [left, right];
      left = {
        type: 'UnionType',
        types,
        line: left.line,
        column: left.column,
      };
    }

    return left;
  }

  private parsePrimaryType(): TypeNode {
    const token = this.peek();
    const line = token.line;
    const column = token.column;

    // Function type: (params) => returnType
    if (this.check(TokenType.LPAREN)) {
      return this.parseFunctionType();
    }

    // Type name (identifier or type keyword)
    let name: string;

    if (this.match(
      TokenType.NOMBRE, TokenType.TEXTE, TokenType.BOOLEEN,
      TokenType.QUELCONQUE, TokenType.INCONNU, TokenType.JAMAIS,
      TokenType.VIDE, TokenType.RIEN, TokenType.TABLEAU, TokenType.PROMESSE
    )) {
      name = this.previous().lexeme;
    } else if (this.match(TokenType.IDENTIFIER)) {
      name = this.previous().lexeme;
    } else {
      throw this.error(token, 'Attendu un type');
    }

    // Generic type: Type<T, U>
    if (this.match(TokenType.LESS)) {
      const typeArguments: TypeNode[] = [];
      do {
        typeArguments.push(this.parseType());
      } while (this.match(TokenType.COMMA));
      this.consume(TokenType.GREATER, "Attendu '>' apres les arguments de type");

      let result: TypeNode = {
        type: 'GenericType',
        name,
        typeArguments,
        line,
        column,
      };

      // Check for array suffix
      while (this.match(TokenType.LBRACKET)) {
        this.consume(TokenType.RBRACKET, "Attendu ']' apres '['");
        result = {
          type: 'ArrayType',
          elementType: result,
          line,
          column,
        };
      }

      return result;
    }

    // Simple type
    let result: TypeNode = {
      type: 'SimpleType',
      name,
      line,
      column,
    };

    // Array type suffix: type[]
    while (this.match(TokenType.LBRACKET)) {
      this.consume(TokenType.RBRACKET, "Attendu ']' apres '['");
      result = {
        type: 'ArrayType',
        elementType: result,
        line,
        column,
      };
    }

    return result;
  }

  private parseFunctionType(): FunctionType {
    const line = this.peek().line;
    const column = this.peek().column;

    this.consume(TokenType.LPAREN, "Attendu '(' pour le type fonction");
    const parameters: FunctionTypeParameter[] = [];

    if (!this.check(TokenType.RPAREN)) {
      do {
        const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de parametre');
        this.consume(TokenType.COLON, "Attendu ':' apres le nom de parametre");
        const typeAnnotation = this.parseType();
        parameters.push({ name: nameToken.lexeme, typeAnnotation });
      } while (this.match(TokenType.COMMA));
    }

    this.consume(TokenType.RPAREN, "Attendu ')' apres les parametres");
    this.consume(TokenType.ARROW, "Attendu '=>' apres les parametres du type fonction");
    const returnType = this.parseType();

    return {
      type: 'FunctionType',
      parameters,
      returnType,
      line,
      column,
    };
  }

  // ============================================================================
  // CLASS DECLARATION
  // ============================================================================

  private classDeclaration(): ClassDeclaration {
    const line = this.previous().line;
    const column = this.previous().column;

    const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de classe');
    const name = nameToken.lexeme;

    let superClass: string | undefined;
    if (this.match(TokenType.ETEND)) {
      const superToken = this.consume(TokenType.IDENTIFIER, "Attendu un nom de classe apres 'etend'");
      superClass = superToken.lexeme;
    }

    let implementsList: string[] | undefined;
    if (this.match(TokenType.IMPLEMENTE)) {
      implementsList = [];
      do {
        const implToken = this.consume(TokenType.IDENTIFIER, "Attendu un nom d'interface apres 'implemente'");
        implementsList.push(implToken.lexeme);
      } while (this.match(TokenType.COMMA));
    }

    this.consume(TokenType.LBRACE, "Attendu '{' pour le corps de la classe");
    const members = this.parseClassMembers();
    this.consume(TokenType.RBRACE, "Attendu '}' apres le corps de la classe");

    return {
      type: 'ClassDeclaration',
      name,
      superClass,
      implements: implementsList,
      members,
      line,
      column,
    };
  }

  private parseClassMembers(): ClassMember[] {
    const members: ClassMember[] = [];

    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      // Skip newlines
      while (this.match(TokenType.NEWLINE)) {}
      if (this.check(TokenType.RBRACE)) break;

      const member = this.parseClassMember();
      if (member) {
        members.push(member);
      }
    }

    return members;
  }

  private parseClassMember(): ClassMember | null {
    const line = this.peek().line;
    const column = this.peek().column;

    // Parse modifiers
    let visibility: Visibility | undefined;
    let isStatic = false;
    let isReadonly = false;
    let isAsync = false;

    // Visibility modifier
    if (this.match(TokenType.PUBLIQUE)) {
      visibility = 'publique';
    } else if (this.match(TokenType.PRIVE)) {
      visibility = 'prive';
    } else if (this.match(TokenType.PROTEGE)) {
      visibility = 'protege';
    }

    // Static modifier
    if (this.check(TokenType.IDENTIFIER) && this.peek().lexeme === 'statique') {
      this.advance();
      isStatic = true;
    }

    // Readonly modifier
    if (this.match(TokenType.LECTURE_SEULE)) {
      isReadonly = true;
    }

    // Async modifier
    if (this.match(TokenType.ASYNCHRONE)) {
      isAsync = true;
    }

    // Constructor
    if (this.match(TokenType.CONSTRUCTEUR)) {
      this.consume(TokenType.LPAREN, "Attendu '(' apres 'constructeur'");
      const params = this.parseFunctionParams();
      this.consume(TokenType.RPAREN, "Attendu ')' apres les parametres");

      this.consume(TokenType.LBRACE, "Attendu '{' pour le corps du constructeur");
      const body = this.blockStatement();

      return {
        type: 'ConstructorDefinition',
        params,
        body,
        line,
        column,
      };
    }

    // Method or property
    if (!this.check(TokenType.IDENTIFIER)) {
      return null;
    }

    const nameToken = this.advance();
    const name = nameToken.lexeme;

    // Method: name(params): returnType { body }
    if (this.check(TokenType.LPAREN)) {
      this.consume(TokenType.LPAREN, "Attendu '(' pour la methode");
      const params = this.parseFunctionParams();
      this.consume(TokenType.RPAREN, "Attendu ')' apres les parametres");

      let returnType: TypeNode | undefined;
      if (this.match(TokenType.COLON)) {
        returnType = this.parseType();
      }

      this.consume(TokenType.LBRACE, "Attendu '{' pour le corps de la methode");
      const body = this.blockStatement();

      return {
        type: 'MethodDefinition',
        name,
        params,
        body,
        async: isAsync,
        static: isStatic,
        visibility,
        returnType,
        line,
        column,
      };
    }

    // Property: name: type = value
    let typeAnnotation: TypeNode | undefined;
    if (this.match(TokenType.COLON)) {
      typeAnnotation = this.parseType();
    }

    let initializer: Expression | undefined;
    if (this.match(TokenType.EQUALS)) {
      initializer = this.expression();
    }

    this.match(TokenType.SEMICOLON, TokenType.NEWLINE);

    return {
      type: 'PropertyDefinition',
      name,
      typeAnnotation,
      initializer,
      visibility,
      readonly: isReadonly,
      static: isStatic,
      line,
      column,
    };
  }

  // ============================================================================
  // FUNCTION DECLARATION
  // ============================================================================

  private functionDeclaration(isAsync: boolean): FunctionDeclaration {
    const line = this.previous().line;
    const column = this.previous().column;

    const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de fonction');
    const name = nameToken.lexeme;

    this.consume(TokenType.LPAREN, "Attendu '(' apres le nom de fonction");
    const params = this.parseFunctionParams();
    this.consume(TokenType.RPAREN, "Attendu ')' apres les parametres");

    let returnType: TypeNode | undefined;
    if (this.match(TokenType.COLON)) {
      returnType = this.parseType();
    }

    this.consume(TokenType.LBRACE, "Attendu '{' pour le corps de la fonction");
    const body = this.blockStatement();

    return {
      type: 'FunctionDeclaration',
      name,
      params,
      body,
      async: isAsync,
      returnType,
      line,
      column,
    };
  }

  private parseFunctionParams(): FunctionParameter[] {
    const params: FunctionParameter[] = [];

    if (!this.check(TokenType.RPAREN)) {
      do {
        const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de parametre');
        let typeAnnotation: TypeNode | undefined;

        if (this.match(TokenType.COLON)) {
          typeAnnotation = this.parseType();
        }

        params.push({ name: nameToken.lexeme, typeAnnotation });
      } while (this.match(TokenType.COMMA));
    }

    return params;
  }

  // ============================================================================
  // VARIABLE DECLARATION
  // ============================================================================

  private variableDeclaration(): VariableDeclaration {
    const kind = this.previous().type === TokenType.CROISSANT ? 'croissant' : 'baguette';
    const line = this.previous().line;
    const column = this.previous().column;

    const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de variable');
    const name = nameToken.lexeme;

    let typeAnnotation: TypeNode | undefined;
    if (this.match(TokenType.COLON)) {
      typeAnnotation = this.parseType();
    }

    let initializer: Expression | undefined;
    if (this.match(TokenType.EQUALS)) {
      initializer = this.expression();
    }

    this.consumeStatementEnd();

    return {
      type: 'VariableDeclaration',
      kind,
      name,
      typeAnnotation,
      initializer,
      line,
      column,
    };
  }

  // ============================================================================
  // STATEMENT PARSING
  // ============================================================================

  private statement(): Statement {
    if (this.match(TokenType.FRANCHEMENT)) {
      return this.ifStatement();
    }

    if (this.match(TokenType.PENDANT)) {
      return this.whileStatement();
    }

    if (this.match(TokenType.POUR)) {
      return this.forStatement();
    }

    if (this.match(TokenType.RETOURNER)) {
      return this.returnStatement();
    }

    if (this.match(TokenType.LBRACE)) {
      return this.blockStatement();
    }

    if (this.match(TokenType.MALHEUREUSEMENT)) {
      return this.tryStatement();
    }

    if (this.match(TokenType.CASSER)) {
      return this.breakStatement();
    }

    if (this.match(TokenType.CONTINUER)) {
      return this.continueStatement();
    }

    if (this.match(TokenType.LANCER)) {
      return this.throwStatement();
    }

    return this.expressionStatement();
  }

  private ifStatement(): IfStatement {
    const line = this.previous().line;
    const column = this.previous().column;

    this.consume(TokenType.LPAREN, "Attendu '(' apres 'franchement'");
    const condition = this.expression();
    this.consume(TokenType.RPAREN, "Attendu ')' apres la condition");

    const thenBranch = this.statement();
    let elseBranch: Statement | undefined;

    if (this.match(TokenType.SINON)) {
      elseBranch = this.statement();
    }

    return {
      type: 'IfStatement',
      condition,
      thenBranch,
      elseBranch,
      line,
      column,
    };
  }

  private whileStatement(): WhileStatement {
    const line = this.previous().line;
    const column = this.previous().column;

    this.consume(TokenType.LPAREN, "Attendu '(' apres 'pendant'");
    const condition = this.expression();
    this.consume(TokenType.RPAREN, "Attendu ')' apres la condition");

    const body = this.statement();

    return {
      type: 'WhileStatement',
      condition,
      body,
      line,
      column,
    };
  }

  private forStatement(): ForStatement {
    const line = this.previous().line;
    const column = this.previous().column;

    this.consume(TokenType.LPAREN, "Attendu '(' apres 'pour'");

    // Initializer
    let initializer: VariableDeclaration | ExpressionStatement | undefined;
    if (this.match(TokenType.SEMICOLON)) {
      initializer = undefined;
    } else if (this.match(TokenType.CROISSANT, TokenType.BAGUETTE)) {
      initializer = this.variableDeclarationNoSemicolon();
      this.consume(TokenType.SEMICOLON, "Attendu ';' apres l'initialiseur");
    } else {
      initializer = {
        type: 'ExpressionStatement',
        expression: this.expression(),
        line: this.previous().line,
        column: this.previous().column,
      };
      this.consume(TokenType.SEMICOLON, "Attendu ';' apres l'initialiseur");
    }

    // Condition
    let condition: Expression | undefined;
    if (!this.check(TokenType.SEMICOLON)) {
      condition = this.expression();
    }
    this.consume(TokenType.SEMICOLON, "Attendu ';' apres la condition");

    // Increment
    let increment: Expression | undefined;
    if (!this.check(TokenType.RPAREN)) {
      increment = this.expression();
    }

    this.consume(TokenType.RPAREN, "Attendu ')' apres la clause for");

    const body = this.statement();

    return {
      type: 'ForStatement',
      initializer,
      condition,
      increment,
      body,
      line,
      column,
    };
  }

  private variableDeclarationNoSemicolon(): VariableDeclaration {
    const kind = this.previous().type === TokenType.CROISSANT ? 'croissant' : 'baguette';
    const line = this.previous().line;
    const column = this.previous().column;

    const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de variable');
    const name = nameToken.lexeme;

    let typeAnnotation: TypeNode | undefined;
    if (this.match(TokenType.COLON)) {
      typeAnnotation = this.parseType();
    }

    let initializer: Expression | undefined;
    if (this.match(TokenType.EQUALS)) {
      initializer = this.expression();
    }

    return {
      type: 'VariableDeclaration',
      kind,
      name,
      typeAnnotation,
      initializer,
      line,
      column,
    };
  }

  private returnStatement(): ReturnStatement {
    const line = this.previous().line;
    const column = this.previous().column;

    let value: Expression | undefined;

    if (!this.check(TokenType.SEMICOLON) && !this.check(TokenType.NEWLINE) && !this.check(TokenType.RBRACE)) {
      value = this.expression();
    }

    this.consumeStatementEnd();

    return {
      type: 'ReturnStatement',
      value,
      line,
      column,
    };
  }

  private blockStatement(): BlockStatement {
    const line = this.previous().line;
    const column = this.previous().column;
    const statements: Statement[] = [];

    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      const stmt = this.declaration();
      if (stmt) {
        statements.push(stmt);
      }
    }

    this.consume(TokenType.RBRACE, "Attendu '}' apres le bloc");

    return {
      type: 'BlockStatement',
      statements,
      line,
      column,
    };
  }

  private tryStatement(): TryStatement {
    const line = this.previous().line;
    const column = this.previous().column;

    this.consume(TokenType.LBRACE, "Attendu '{' apres 'malheureusement'");
    const tryBlock = this.blockStatement();

    let catchParam: string | undefined;
    let catchBlock: BlockStatement | undefined;
    let finallyBlock: BlockStatement | undefined;

    if (this.match(TokenType.MERDE)) {
      if (this.match(TokenType.LPAREN)) {
        const paramToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de parametre catch');
        catchParam = paramToken.lexeme;
        this.consume(TokenType.RPAREN, "Attendu ')' apres le parametre catch");
      }
      this.consume(TokenType.LBRACE, "Attendu '{' apres 'merde'");
      catchBlock = this.blockStatement();
    }

    if (this.match(TokenType.QUANDMEME)) {
      this.consume(TokenType.LBRACE, "Attendu '{' apres 'quandmeme'");
      finallyBlock = this.blockStatement();
    }

    if (!catchBlock && !finallyBlock) {
      throw this.error(this.peek(), "Un bloc try doit avoir au moins un bloc 'merde' ou 'quandmeme'");
    }

    return {
      type: 'TryStatement',
      tryBlock,
      catchParam,
      catchBlock,
      finallyBlock,
      line,
      column,
    };
  }

  private breakStatement(): BreakStatement {
    const line = this.previous().line;
    const column = this.previous().column;
    this.consumeStatementEnd();

    return {
      type: 'BreakStatement',
      line,
      column,
    };
  }

  private continueStatement(): ContinueStatement {
    const line = this.previous().line;
    const column = this.previous().column;
    this.consumeStatementEnd();

    return {
      type: 'ContinueStatement',
      line,
      column,
    };
  }

  private throwStatement(): ThrowStatement {
    const line = this.previous().line;
    const column = this.previous().column;

    const argument = this.expression();
    this.consumeStatementEnd();

    return {
      type: 'ThrowStatement',
      argument,
      line,
      column,
    };
  }

  private expressionStatement(): ExpressionStatement {
    const expr = this.expression();
    const line = expr.line;
    const column = expr.column;

    this.consumeStatementEnd();

    return {
      type: 'ExpressionStatement',
      expression: expr,
      line,
      column,
    };
  }

  private consumeStatementEnd(): void {
    // Allow semicolon, newline, or implicit end (before } or EOF)
    if (this.match(TokenType.SEMICOLON, TokenType.NEWLINE)) {
      // Skip any additional newlines
      while (this.match(TokenType.NEWLINE)) {}
      return;
    }

    // Implicit end before closing brace or EOF is OK
    if (this.check(TokenType.RBRACE) || this.isAtEnd()) {
      return;
    }

    // Otherwise, it's an error
    throw this.error(this.peek(), "Attendu ';' ou nouvelle ligne apres l'instruction");
  }

  // ============================================================================
  // EXPRESSION PARSING - Operator Precedence (lowest to highest)
  // ============================================================================

  private expression(): Expression {
    return this.assignment();
  }

  // Assignment: =, +=, -=
  private assignment(): Expression {
    const expr = this.or();

    if (this.match(TokenType.EQUALS, TokenType.PLUS_EQUALS, TokenType.MINUS_EQUALS)) {
      const operator = this.previous();
      const value = this.assignment(); // Right-associative

      if (expr.type === 'Identifier' || expr.type === 'MemberExpression') {
        let op: '=' | '+=' | '-=';
        switch (operator.type) {
          case TokenType.EQUALS:
            op = '=';
            break;
          case TokenType.PLUS_EQUALS:
            op = '+=';
            break;
          case TokenType.MINUS_EQUALS:
            op = '-=';
            break;
          default:
            op = '=';
        }

        return {
          type: 'AssignmentExpression',
          target: expr,
          operator: op,
          value,
          line: expr.line,
          column: expr.column,
        };
      }

      throw this.error(operator, "Cible d'affectation invalide");
    }

    return expr;
  }

  // Logical OR: ou, ||
  private or(): Expression {
    let expr = this.and();

    while (this.match(TokenType.OU, TokenType.PIPE)) {
      // Check for double pipe
      if (this.previous().type === TokenType.PIPE && this.match(TokenType.PIPE)) {
        // It was ||
      }
      const operator = 'ou';
      const right = this.and();
      expr = {
        type: 'BinaryExpression',
        left: expr,
        operator,
        right,
        line: expr.line,
        column: expr.column,
      };
    }

    return expr;
  }

  // Logical AND: et, &&
  private and(): Expression {
    let expr = this.equality();

    while (this.match(TokenType.ET, TokenType.AMPERSAND)) {
      // Check for double ampersand
      if (this.previous().type === TokenType.AMPERSAND && this.match(TokenType.AMPERSAND)) {
        // It was &&
      }
      const operator = 'et';
      const right = this.equality();
      expr = {
        type: 'BinaryExpression',
        left: expr,
        operator,
        right,
        line: expr.line,
        column: expr.column,
      };
    }

    return expr;
  }

  // Equality: est, nest_pas, ==, !=
  private equality(): Expression {
    let expr = this.comparison();

    while (this.match(TokenType.EST, TokenType.NEST_PAS, TokenType.DOUBLE_EQUALS, TokenType.NOT_EQUALS)) {
      const operatorToken = this.previous();
      let operator: string;
      switch (operatorToken.type) {
        case TokenType.EST:
        case TokenType.DOUBLE_EQUALS:
          operator = 'est';
          break;
        case TokenType.NEST_PAS:
        case TokenType.NOT_EQUALS:
          operator = 'nest_pas';
          break;
        default:
          operator = operatorToken.lexeme;
      }
      const right = this.comparison();
      expr = {
        type: 'BinaryExpression',
        left: expr,
        operator,
        right,
        line: expr.line,
        column: expr.column,
      };
    }

    return expr;
  }

  // Comparison: <, >, <=, >=
  private comparison(): Expression {
    let expr = this.term();

    while (this.match(TokenType.LESS, TokenType.GREATER, TokenType.LESS_EQUALS, TokenType.GREATER_EQUALS)) {
      const operator = this.previous().lexeme;
      const right = this.term();
      expr = {
        type: 'BinaryExpression',
        left: expr,
        operator,
        right,
        line: expr.line,
        column: expr.column,
      };
    }

    return expr;
  }

  // Term: +, -
  private term(): Expression {
    let expr = this.factor();

    while (this.match(TokenType.PLUS, TokenType.MINUS)) {
      const operator = this.previous().lexeme;
      const right = this.factor();
      expr = {
        type: 'BinaryExpression',
        left: expr,
        operator,
        right,
        line: expr.line,
        column: expr.column,
      };
    }

    return expr;
  }

  // Factor: *, /, %
  private factor(): Expression {
    let expr = this.unary();

    while (this.match(TokenType.STAR, TokenType.SLASH, TokenType.PERCENT)) {
      const operator = this.previous().lexeme;
      const right = this.unary();
      expr = {
        type: 'BinaryExpression',
        left: expr,
        operator,
        right,
        line: expr.line,
        column: expr.column,
      };
    }

    return expr;
  }

  // Unary: pas, -, !
  private unary(): Expression {
    if (this.match(TokenType.PAS, TokenType.BANG)) {
      const line = this.previous().line;
      const column = this.previous().column;
      const operand = this.unary();
      return {
        type: 'UnaryExpression',
        operator: 'pas',
        operand,
        line,
        column,
      };
    }

    if (this.match(TokenType.MINUS)) {
      const line = this.previous().line;
      const column = this.previous().column;
      const operand = this.unary();
      return {
        type: 'UnaryExpression',
        operator: '-',
        operand,
        line,
        column,
      };
    }

    if (this.match(TokenType.ATTENDRE)) {
      const line = this.previous().line;
      const column = this.previous().column;
      const argument = this.unary();
      return {
        type: 'AwaitExpression',
        argument,
        line,
        column,
      };
    }

    return this.postfixExpression();
  }

  // Postfix: call and member access, type assertions
  private postfixExpression(): Expression {
    let expr = this.primary();

    while (true) {
      if (this.match(TokenType.LPAREN)) {
        expr = this.finishCall(expr);
      } else if (this.match(TokenType.DOT)) {
        const property = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de propriete apres "."');
        expr = {
          type: 'MemberExpression',
          object: expr,
          property: {
            type: 'Identifier',
            name: property.lexeme,
            line: property.line,
            column: property.column,
          },
          computed: false,
          line: expr.line,
          column: expr.column,
        };
      } else if (this.match(TokenType.LBRACKET)) {
        const index = this.expression();
        this.consume(TokenType.RBRACKET, 'Attendu "]" apres l\'index');
        expr = {
          type: 'MemberExpression',
          object: expr,
          property: index,
          computed: true,
          line: expr.line,
          column: expr.column,
        };
      } else if (this.match(TokenType.COMME)) {
        // Type assertion: expr comme Type
        const typeAnnotation = this.parseType();
        expr = {
          type: 'TypeAssertion',
          expression: expr,
          typeAnnotation,
          line: expr.line,
          column: expr.column,
        };
      } else {
        break;
      }
    }

    return expr;
  }

  private finishCall(callee: Expression): CallExpression {
    const args: Expression[] = [];

    if (!this.check(TokenType.RPAREN)) {
      do {
        args.push(this.expression());
      } while (this.match(TokenType.COMMA));
    }

    this.consume(TokenType.RPAREN, 'Attendu ")" apres les arguments');

    return {
      type: 'CallExpression',
      callee,
      arguments: args,
      line: callee.line,
      column: callee.column,
    };
  }

  // Primary expressions
  private primary(): Expression {
    const token = this.peek();
    const line = token.line;
    const column = token.column;

    // Boolean literals
    if (this.match(TokenType.OUI)) {
      return { type: 'Literal', value: true, line, column };
    }
    if (this.match(TokenType.NON)) {
      return { type: 'Literal', value: false, line, column };
    }

    // Null literal
    if (this.match(TokenType.RIEN)) {
      return { type: 'Literal', value: null, line, column };
    }

    // Number literal
    if (this.match(TokenType.NUMBER)) {
      const value = this.previous().literal;
      return { type: 'Literal', value: value ?? 0, line, column };
    }

    // String literal
    if (this.match(TokenType.STRING)) {
      const value = this.previous().literal;
      return { type: 'Literal', value: value ?? '', line, column };
    }

    // This reference
    if (this.match(TokenType.CECI)) {
      return { type: 'ThisExpression', line, column };
    }

    // New expression
    if (this.match(TokenType.NOUVEAU)) {
      return this.newExpression();
    }

    // Arrow function or grouping
    if (this.check(TokenType.LPAREN)) {
      return this.parenExpressionOrArrowFunction();
    }

    // Async arrow function: asynchrone (params) => ...
    if (this.match(TokenType.ASYNCHRONE)) {
      if (this.check(TokenType.LPAREN)) {
        return this.arrowFunction(true);
      }
      throw this.error(this.peek(), "Attendu '(' apres 'asynchrone' pour une fonction flechee");
    }

    // Array literal
    if (this.match(TokenType.LBRACKET)) {
      return this.arrayExpression();
    }

    // Object literal
    if (this.match(TokenType.LBRACE)) {
      return this.objectExpression();
    }

    // Identifier
    if (this.match(TokenType.IDENTIFIER)) {
      const name = this.previous().lexeme;

      // Check for arrow function: identifier => ...
      if (this.check(TokenType.ARROW)) {
        this.advance(); // consume =>
        return this.finishArrowFunction([{ name }], false);
      }

      return { type: 'Identifier', name, line, column };
    }

    throw this.error(token, 'Expression attendue');
  }

  private newExpression(): NewExpression {
    const line = this.previous().line;
    const column = this.previous().column;

    // Parse the class/constructor being called
    let callee: Expression;

    if (this.check(TokenType.IDENTIFIER)) {
      const token = this.advance();
      callee = {
        type: 'Identifier',
        name: token.lexeme,
        line: token.line,
        column: token.column,
      };
    } else {
      throw this.error(this.peek(), "Attendu un nom de classe apres 'nouveau'");
    }

    // Handle member access on the constructor (e.g., nouveau Module.Class())
    while (this.match(TokenType.DOT)) {
      const property = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de propriete');
      callee = {
        type: 'MemberExpression',
        object: callee,
        property: {
          type: 'Identifier',
          name: property.lexeme,
          line: property.line,
          column: property.column,
        },
        computed: false,
        line: callee.line,
        column: callee.column,
      };
    }

    // Parse constructor arguments
    const args: Expression[] = [];
    if (this.match(TokenType.LPAREN)) {
      if (!this.check(TokenType.RPAREN)) {
        do {
          args.push(this.expression());
        } while (this.match(TokenType.COMMA));
      }
      this.consume(TokenType.RPAREN, 'Attendu ")" apres les arguments');
    }

    return {
      type: 'NewExpression',
      callee,
      arguments: args,
      line,
      column,
    };
  }

  private parenExpressionOrArrowFunction(): Expression {
    const startToken = this.peek();
    const line = startToken.line;
    const column = startToken.column;

    // Try to parse as arrow function parameters
    const checkpoint = this.current;

    try {
      this.advance(); // consume '('

      // Empty params: () => ...
      if (this.check(TokenType.RPAREN)) {
        this.advance(); // consume ')'
        if (this.check(TokenType.ARROW)) {
          this.advance(); // consume '=>'
          return this.finishArrowFunction([], false);
        }
        // Not an arrow function - was empty parens ()
        throw new Error('backtrack');
      }

      // Try to parse params
      const params: ArrowFunctionParameter[] = [];
      let isArrowFunction = true;

      do {
        if (!this.check(TokenType.IDENTIFIER)) {
          isArrowFunction = false;
          break;
        }

        const nameToken = this.advance();
        let typeAnnotation: TypeNode | undefined;

        if (this.match(TokenType.COLON)) {
          typeAnnotation = this.parseType();
        }

        params.push({ name: nameToken.lexeme, typeAnnotation });
      } while (this.match(TokenType.COMMA));

      if (isArrowFunction && this.check(TokenType.RPAREN)) {
        this.advance(); // consume ')'

        // Check for return type annotation
        let returnType: TypeNode | undefined;
        if (this.match(TokenType.COLON)) {
          returnType = this.parseType();
        }

        if (this.check(TokenType.ARROW)) {
          this.advance(); // consume '=>'
          return this.finishArrowFunction(params, false, returnType);
        }
      }

      // Not an arrow function, backtrack
      throw new Error('backtrack');
    } catch {
      // Backtrack and parse as grouping expression
      this.current = checkpoint;
    }

    // Parse as grouping expression
    this.advance(); // consume '('
    const expr = this.expression();
    this.consume(TokenType.RPAREN, 'Attendu ")" apres l\'expression');

    return {
      type: 'GroupingExpression',
      expression: expr,
      line,
      column,
    };
  }

  private arrowFunction(isAsync: boolean): ArrowFunction {
    const line = this.previous().line;
    const column = this.previous().column;

    this.consume(TokenType.LPAREN, "Attendu '(' pour les parametres");
    const params: ArrowFunctionParameter[] = [];

    if (!this.check(TokenType.RPAREN)) {
      do {
        const nameToken = this.consume(TokenType.IDENTIFIER, 'Attendu un nom de parametre');
        let typeAnnotation: TypeNode | undefined;

        if (this.match(TokenType.COLON)) {
          typeAnnotation = this.parseType();
        }

        params.push({ name: nameToken.lexeme, typeAnnotation });
      } while (this.match(TokenType.COMMA));
    }

    this.consume(TokenType.RPAREN, "Attendu ')' apres les parametres");

    let returnType: TypeNode | undefined;
    if (this.match(TokenType.COLON)) {
      returnType = this.parseType();
    }

    this.consume(TokenType.ARROW, "Attendu '=>' pour la fonction flechee");

    return this.finishArrowFunction(params, isAsync, returnType);
  }

  private finishArrowFunction(
    params: ArrowFunctionParameter[],
    isAsync: boolean,
    returnType?: TypeNode
  ): ArrowFunction {
    const line = this.previous().line;
    const column = this.previous().column;

    let body: Expression | BlockStatement;

    if (this.match(TokenType.LBRACE)) {
      body = this.blockStatement();
    } else {
      body = this.expression();
    }

    return {
      type: 'ArrowFunction',
      params,
      body,
      async: isAsync,
      returnType,
      line,
      column,
    };
  }

  private arrayExpression(): ArrayExpression {
    const line = this.previous().line;
    const column = this.previous().column;
    const elements: Expression[] = [];

    if (!this.check(TokenType.RBRACKET)) {
      do {
        // Skip newlines inside array
        while (this.match(TokenType.NEWLINE)) {}
        if (this.check(TokenType.RBRACKET)) break;

        elements.push(this.expression());

        // Skip newlines after element
        while (this.match(TokenType.NEWLINE)) {}
      } while (this.match(TokenType.COMMA));
    }

    // Skip trailing newlines
    while (this.match(TokenType.NEWLINE)) {}
    this.consume(TokenType.RBRACKET, 'Attendu "]" apres les elements du tableau');

    return {
      type: 'ArrayExpression',
      elements,
      line,
      column,
    };
  }

  private objectExpression(): ObjectExpression {
    const line = this.previous().line;
    const column = this.previous().column;
    const properties: ObjectProperty[] = [];

    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      // Skip newlines
      while (this.match(TokenType.NEWLINE)) {}
      if (this.check(TokenType.RBRACE)) break;

      let key: Expression;
      let computed = false;
      let shorthand = false;

      // Computed property: [expr]: value
      if (this.match(TokenType.LBRACKET)) {
        key = this.expression();
        this.consume(TokenType.RBRACKET, 'Attendu "]" apres la cle calculee');
        computed = true;
      }
      // String key: "key": value
      else if (this.check(TokenType.STRING)) {
        const token = this.advance();
        key = {
          type: 'Literal',
          value: token.literal ?? token.lexeme.slice(1, -1),
          line: token.line,
          column: token.column,
        };
      }
      // Identifier key: key: value OR shorthand: key
      else {
        const token = this.consume(TokenType.IDENTIFIER, "Attendu une cle d'objet");
        key = {
          type: 'Identifier',
          name: token.lexeme,
          line: token.line,
          column: token.column,
        };

        // Shorthand property: { x } means { x: x }
        if (!this.check(TokenType.COLON)) {
          shorthand = true;
          properties.push({
            key,
            value: key,
            computed: false,
            shorthand: true,
          });

          // Skip comma and continue
          this.match(TokenType.COMMA, TokenType.NEWLINE);
          continue;
        }
      }

      this.consume(TokenType.COLON, 'Attendu ":" apres la cle');
      const value = this.expression();

      properties.push({ key, value, computed, shorthand });
      this.match(TokenType.COMMA, TokenType.NEWLINE);
    }

    // Skip trailing newlines
    while (this.match(TokenType.NEWLINE)) {}
    this.consume(TokenType.RBRACE, 'Attendu "}" apres les proprietes de l\'objet');

    return {
      type: 'ObjectExpression',
      properties,
      line,
      column,
    };
  }
}

// Re-export AST types
export * from './ast.js';
